import React from 'react';

import { Container } from '@/components/ui';
import { FREQUENCY_OPTIONS } from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

export interface ScanningFrequencySelectorProps {
  selectedFrequency: 'daily' | 'weekly' | 'monthly';
  onFrequencyChange: (frequency: 'daily' | 'weekly' | 'monthly') => void;
  disabled?: boolean;
  className?: string;
}

/**
 * ScanningFrequencySelector - Reusable component for scanning frequency selection
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export const ScanningFrequencySelector: React.FC<
  ScanningFrequencySelectorProps
> = ({ selectedFrequency, onFrequencyChange, disabled = false, className }) => {
  return (
    <section
      className={cn('space-y-4', className)}
      aria-labelledby="frequency-section-title"
    >
      <h3
        id="frequency-section-title"
        className="text-chariot-text-primary text-lg font-semibold"
      >
        Scanning Frequency
      </h3>
      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        role="radiogroup"
        aria-labelledby="frequency-section-title"
        aria-describedby="frequency-description"
      >
        <p id="frequency-description" className="sr-only">
          Choose how often security scans should be performed. Higher frequency
          increases pricing.
        </p>
        {FREQUENCY_OPTIONS.map(option => {
          const isSelected = selectedFrequency === option.value;

          return (
            <Container
              key={option.value}
              onClick={() =>
                !disabled &&
                onFrequencyChange(
                  option.value as 'daily' | 'weekly' | 'monthly'
                )
              }
              className={cn(
                'relative cursor-pointer py-4 text-center',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              role="radio"
              isSelected={isSelected}
              aria-checked={isSelected}
              aria-describedby={`frequency-${option.value}-desc`}
              data-testid={`frequency-${option.value}`}
            >
              {isSelected && (
                <div className="bg-chariot-brand-primary absolute -top-3 left-1/2 -translate-x-1/2 transform rounded-full px-3 py-1 text-xs font-bold text-white">
                  YOUR PLAN
                </div>
              )}
              <span className="text-base font-medium">{option.label}</span>
            </Container>
          );
        })}
      </div>
    </section>
  );
};
